import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from "@angular/router";
import { forkJoin, of, Subject } from "rxjs";
import {catchError, finalize, map, switchMap, takeUntil, tap} from "rxjs/operators";
import { MessageService } from "primeng/api";

// Services / Subjects
import { FileService } from "../../../services/file.service";
import { FeatureService } from "../../../services/feature.service";
import { PropertyService } from "../../../services/property.service";
import { PropertyInitialSubject } from "../../../subject/property.initial.subject";
import { FlowModeSubject } from "../../../subject/flow.mode.subject";
import { FlowService } from "../../../services/flow.service";
import { FlowStateService } from "../../../services/flow.state.service";
import { SegmentSubjectService } from "../../../subject/segment.subject";

// DTOs / Enums
import { FileInformation } from "../../../dto/file.information.dto";
import { PropertyInitialResponseDto } from "../../../dto/property.initial.response.dto";
import { FlowMode } from "../../../dto/enum/flow.mode.enum";
import { RecordStageEnum } from "../../../dto/enum/record.stage.enum";

const STEP_KEY = 'pictures';
type MediaType = 'image' | 'video';

interface MediaFile {
    id: number;
    name: string;
    url: string;
    previewUrl?: string;
    type: MediaType;   // ← NUEVO
    size?: number;
    broken?: boolean;
}

interface MediaCategory {
    featureId: number;
    label: string;
    mandatory: number | boolean;
    uploading?: boolean; // ← NUEVO
    images: MediaFile[];
}


@Component({
    selector: 'app-property-pictures',
    templateUrl: './property-pictures.component.html',
    styleUrls: ['./property-pictures.component.scss']
})
export class PropertyPicturesComponent implements OnInit, OnDestroy {

    // --------- Flujo / navegación -------------
    currentMode: FlowMode = FlowMode.CREATE;
    previousUrl = '';
    nextUrl = '';

    // --------- Propiedad / backend -------------
    propertyInitial: PropertyInitialResponseDto | null = null;
    propertyId: number | undefined;
    segmentLabel: string | undefined = '';

    // --------- UI / estado -------------
    loading = true;
    showErrorDialog = false;
    dialogCode = '';
    dialogMessage = '';
    private destroy$ = new Subject<void>();

    // --------- Datos del paso -------------
    imageCategories: MediaCategory[] = [];

    maxFileSize = 10 * 1024 * 1024; // 10 MB (ajústalo al real)


    constructor(
        private router: Router,
        private fileService: FileService,
        private featureService: FeatureService,
        private propertyService: PropertyService,
        private propertyInitialSubject: PropertyInitialSubject,
        private flowModeSubject: FlowModeSubject,
        private flowService: FlowService,
        private flowState: FlowStateService,
        private segmentSubject: SegmentSubjectService,
        private messageService: MessageService
    ) {}

    // -----------------------------------------------------------
    // Ciclo de vida
    // -----------------------------------------------------------
    ngOnInit(): void {
        // 1) Modo y rutas
        const mode = this.flowModeSubject.getCurrentMode();
        if (!mode) { this.router.navigate(['/list-pending']); return; }
        this.currentMode = mode;
        this.previousUrl = this.flowService.getPreviousStep(STEP_KEY);
        this.nextUrl = this.flowService.getNextStep(STEP_KEY);

        // 2) Paso actual
        this.flowState.setCurrentStep(STEP_KEY);

        // 3) Propiedad y stage
        this.propertyInitial = this.propertyInitialSubject.getCurrentProperty();
        if (!this.propertyInitial?.propertyId) {
            this.router.navigate(['/list-pending']);
            return;
        }
        this.propertyId = this.propertyInitial.propertyId;
        this.segmentLabel = this.resolveSegmentLabel();
        if (this.propertyInitial.recordStage) {
            this.flowState.setRecordStage(this.propertyInitial.recordStage as RecordStageEnum);
        }
        this.flowState.setPropertyId(this.propertyId);

        // 4) Cargar categorías e imágenes
        this.loadUploadedImages();
    }

    ngOnDestroy(): void {
        this.imageCategories.forEach((category: MediaCategory) => {
            (category.images || []).forEach((file: MediaFile) => {
                if (file.previewUrl) {
                    URL.revokeObjectURL(file.previewUrl);
                }
            });
        });
        this.destroy$.next();
        this.destroy$.complete();
    }

    // -----------------------------------------------------------
    // Carga de imágenes
    // -----------------------------------------------------------
    private loadUploadedImages(): void {
        this.featureService.getMultimediaPropertyList(this.propertyId).pipe(
            tap((response: any[]) => {
                this.imageCategories = (response || []).map((feature: any): MediaCategory => ({
                    featureId: feature.featureId,
                    label: feature.featureLabel,
                    mandatory: feature.mandatory,
                    uploading: false,
                    images: (feature.fileList || []).map((f: any): MediaFile => ({
                        id: f.fileId,
                        name: f.fileName || f.fileCategory,
                        url: f.fileUrl,
                        type: this.guessType(f.mimeType || f.fileName || f.fileUrl),
                        size: f.size
                    }))
                }));
                this.persistToFlowState();
            }),
            catchError((err) => {
                console.error('Error al cargar multimedia:', err);
                this.setErrorDialog('Error multimedia', 'No se pudieron cargar las categorías o imágenes.');
                return of([]);
            }),
            finalize(() => {
                this.loading = false;
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    // -----------------------------------------------------------
    // Upload / Delete
    // -----------------------------------------------------------
    onFileSelect(event: any, category: any): void {
        if (!this.propertyId) return;

        const files: File[] = Array.from(event?.files ?? []);
        if (!files.length) return;

        // Validación simple local:
        const invalids: string[] = [];
        const allowed = /\.(jpeg|jpg|png|mp4|mov)$/i;
        files.forEach(f => {
            if (!allowed.test(f.name)) invalids.push(`${f.name}: formato inválido`);
            // tamaño ya está validado por p-fileUpload, pero puedes agregar checks extra
        });
        if (invalids.length) {
            this.messageService.add({ severity: 'warn', summary: 'Validación', detail: invalids.join(' | '), life: 6000 });
            return;
        }

        const requests = files.map((file: File) =>
            this.fileService.uploadImage(this.propertyId!, category.featureId, file)
        );

        forkJoin(requests).pipe(
            catchError((err) => {
                console.error('Error al subir archivos:', err);
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudieron subir los archivos.' });
                return of([] as FileInformation[]);
            }),
            takeUntil(this.destroy$)
        ).subscribe((responses: FileInformation[]) => {
            responses.forEach((res) => {
                category.images.push({ id: res.fileId, name: res.fileName, url: res.fileUrl });
            });

            this.persistToFlowState();
            this.messageService.add({ severity: 'success', summary: 'Archivos subidos', detail: 'Subida exitosa.' });
        });
    }

    // -----------------------------------------------------------
    // Cache local (FlowState) — opcional (conteo por categoría)
    // -----------------------------------------------------------
    private persistToFlowState(): void {
        const counts = this.imageCategories.map(c => ({ featureId: c.featureId, count: c.images.length }));
        this.flowState.setStepData(STEP_KEY, { counts });
    }

    // -----------------------------------------------------------
    // Navegación / Validación / Avance de etapa
    // -----------------------------------------------------------
    handleNextStep(): void {
        // Validar: todas las categorías obligatorias deben tener al menos 1 archivo
        const incomplete = this.imageCategories.filter(c => !!c.mandatory && (!c.images || c.images.length === 0));
        if (incomplete.length) {
            this.messageService.add({
                severity: 'error',
                summary: 'Faltan imágenes',
                detail: 'Debes cargar al menos una imagen para cada categoría obligatoria.'
            });
            return;
        }

        // marcar stage local
        this.flowState.setRecordStage(RecordStageEnum.MULTIMEDIA);

        if (this.shouldAdvanceStage()) {
            const body = { id: this.propertyId };
            this.propertyService.nextStageProperty(body).pipe(
                catchError((err) => { console.error('nextStage error', err); return of(null); }),
                takeUntil(this.destroy$)
            ).subscribe(() => this.router.navigate([this.nextUrl]));
            return;
        }

        this.router.navigate([this.nextUrl]);
    }

    handleSaveAndExit(): void {
        // Guardar y salir NO avanza etapa (ya subiste/borraste directo al backend)
        this.router.navigate(['/list-pending']); // o dashboard
    }

    handlePreviousStep(): void {
        this.router.navigate([this.previousUrl]);
    }

    private shouldAdvanceStage(): boolean {
        if (this.currentMode === FlowMode.EDIT) return false;
        if (this.currentMode === FlowMode.CREATE) return true;

        // RESUME: avanzar solo si estás después del recordStage
        const stage = this.flowState.recordStage;
        if (!stage) return false;

        const stageStep = this.flowState.getStepByRecordStage(stage);
        const currentIdx = this.flowService.getStepIndex(STEP_KEY);
        const stageIdx = this.flowService.getStepIndex(stageStep);

        if (currentIdx === -1 || stageIdx === -1) return false;
        return currentIdx > stageIdx;
    }

    // -----------------------------------------------------------
    // Errores UI
    // -----------------------------------------------------------
    private setErrorDialog(code: string, message: string): void {
        this.showErrorDialog = true;
        this.dialogCode = code;
        this.dialogMessage = message;
    }
    private guessType(nameOrMime?: string): MediaType {
        const s = (nameOrMime || '').toLowerCase();
        return s.includes('png') || s.includes('jpg') || s.includes('jpeg') || s.startsWith('image/')
            ? 'image'
            : 'video';
    }

    private extractFiles(event: any): File[] {
        const c = event?.files ?? event?.currentFiles ?? event?.target?.files ?? [];
        if (Array.isArray(c)) return c;
        if (c instanceof FileList) return Array.from(c);
        if (c instanceof File) return [c];
        return [];
    }

    onSelectValidate(event: any): void {
        const files = this.extractFiles(event);
        if (!files.length) return;

        const invalids: string[] = [];
        const allowed = /\.(jpeg|jpg|png|mp4|mov)$/i;

        for (const f of files) {
            if (!allowed.test(f.name)) invalids.push(`${f.name}: formato inválido`);
            if (f.size > this.maxFileSize) invalids.push(`${f.name}: excede ${(this.maxFileSize/1024/1024)} MB`);
        }

        if (invalids.length) {
            this.messageService.add({ severity: 'warn', summary: 'Validación', detail: invalids.join(' | '), life: 6000 });
        }
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
    }

    onDrop(event: DragEvent, category: MediaCategory, uploader: any): void {
        event.preventDefault();
        event.stopPropagation();
        const files = Array.from(event.dataTransfer?.files || []);
        if (!files.length) return;
        this.onSelectValidate({ files });
        this.onUpload({ files }, category, uploader);
    }

    /** SUBIR + RENOMBRAR (PUT /file/title/:id) + AGREGAR A LA LISTA */
    onUpload(event: any, category: MediaCategory, uploader: any): void {
        if (!this.propertyId) return;
        const files = this.extractFiles(event);
        if (!files.length) return;

        category.uploading = true;

        forkJoin(files.map(file => this.fileService.uploadImage(this.propertyId!, category.featureId, file)))
            .pipe(
                switchMap(uploaded =>
                    forkJoin(uploaded.map((res, i) =>
                        this.fileService.putFileName(res.fileId, { data: files[i].name }).pipe(
                            catchError(() => of(null)),
                            map((): MediaFile => ({
                                id: res.fileId,
                                name: files[i].name ?? res.fileName,
                                url: res.fileUrl,
                                previewUrl: URL.createObjectURL(files[i]),
                                type: this.guessType(files[i].type || files[i].name), // ← asignar
                                size: files[i].size
                            }))
                        )
                    ))
                ),
                finalize(() => (category.uploading = false)),
                catchError(() => of([]))
            )
            .subscribe((finalFiles: MediaFile[]) => {
                finalFiles.filter(Boolean).forEach(f => category.images.push(f));
                uploader?.clear?.();
                this.persistToFlowState();
                this.messageService.add({ severity: 'success', summary: 'Listo', detail: 'Archivo(s) subido(s) correctamente.' });
            });
    }


    /** ELIMINAR EN BACKEND Y ACTUALIZAR UI */
    onDeleteImage(event: any, category: MediaCategory): void {
        const fileObj = event?.file || event;
        const fileToRemove = category.images.find(f => f.id === fileObj?.id);
        if (!fileToRemove) return;

        this.fileService.deleteFile(fileToRemove.id).pipe(
            catchError(() => { this.messageService.add({ severity:'error', summary:'Error', detail:'No se pudo eliminar.' }); return of(null); })
        ).subscribe(() => {
            category.images = category.images.filter(f => f.id !== fileToRemove.id);
            this.persistToFlowState();
            this.messageService.add({ severity:'success', summary:'Archivo eliminado', detail:fileToRemove.name });
        });
    }


    /* trackBys */
    trackByCat = (_: number, c: any) => c.featureId;
    trackByFile = (_: number, f: any) => f.id;

    markBroken(file: MediaFile): void {
        file.broken = true;
    }

    private resolveSegmentLabel(): string {
        return this.propertyInitial?.segmentLabel
            || this.segmentSubject.getSelectedSegment()?.segmentLabel
            || 'propiedad';
    }
}
